// built-from: /facilitation-outcome-projection v1 impl:facilitation-outcome-projection role:implementation
import type React from 'react';
import VisionOutcomeSurface from '../components/vision/VisionSurface';
import EngagementCharterSurface from './EngagementCharterSurface';
import {
  ActivityNotesSurface,
  BriefOutcomeSurface,
  BusinessMapSurface,
  ObjectivesOutcomeSurface,
} from '../engagement/OutlineSurfaces';
import type { OutcomeSurfaceProps } from './surfaceBinding';
import type { FacilitationChatCopy } from '../hooks/useChat';

export type { OutcomeSurfaceProps } from './surfaceBinding';

export interface SurfaceDefinition {
  id: string;
  title: string;
  workspaceLabel: string;
  chatLabel: string;
  emptyChatText: string;
  about: string[];
  chatCopy: FacilitationChatCopy;
  component: React.ComponentType<OutcomeSurfaceProps>;
  // 100 (owner decision 7): the surface consumes `highlightFactId` and can go
  // to a fact the reply names, so its captured facts read as links. A surface
  // that cannot lists them as plain text.
  canHighlight?: boolean;
  // 077: per-surface voice posture on the ONE chat. speakReplies false is the
  // listening posture (the Brief): tools still run, replies are not spoken.
  voice?: { speakReplies: boolean };
}

const surfaces: Record<string, SurfaceDefinition> = {
  'vision-surface': {
    id: 'vision-surface',
    title: 'Vision articulation',
    workspaceLabel: 'Working vision',
    chatLabel: 'Conversation',
    emptyChatText: 'Start the conversation. The working vision will take shape beside it.',
    about: [
      "The approach draws on two established disciplines. Richard Rumelt's work on strategy: diagnose the real challenge before setting goals. And benefits realisation practice: a benefit is real once it has a named owner, a way to measure it, and a baseline to measure from.",
      'Most sessions take 30 to 45 minutes. Progress saves automatically, so you can leave at any point and resume where you stopped.',
      'The working vision remains editable throughout. Nothing inferred from conversation, voice, or documents is confirmed without review.',
    ],
    chatCopy: {
      openingFallback: (hour) => {
        const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
        return `${greeting}. We're here to work out where the organisation needs to get to, and what stands in the way. `
          + 'The working vision beside our conversation develops as we talk, and everything waits for your say-so. '
          + 'To start: from your perspective, what is the biggest problem the organisation is facing right now?';
      },
      sendFailure: 'Your message was not sent. The working vision is unchanged; try again when the connection returns.',
      resumeFallback: 'Welcome back. I have reopened the working vision so we can continue from where we left off.',
      resumeFailure: 'We could not resume the conversation. Your working vision is still available; try again when the connection returns.',
    },
    component: VisionOutcomeSurface,
    canHighlight: true,
  },
  'engagement-charter-surface': {
    id: 'engagement-charter-surface',
    title: 'Engagement brief',
    workspaceLabel: 'Engagement brief',
    chatLabel: 'Brief conversation',
    emptyChatText: 'Describe what the client wants. Suggestions will appear beside the conversation.',
    about: [
      'This conversation covers what the client wants, why now, how they will judge success, and what is in or out of scope.',
      'ArcPilot suggests wording. Review every suggestion before adding it to the brief.',
    ],
    chatCopy: {
      openingFallback: () => 'What outcome or problem has the client asked this engagement to address?',
      sendFailure: 'Your message was not sent. The engagement brief has not changed. Try again.',
      resumeFallback: 'Continue drafting the engagement brief.',
      resumeFailure: 'We could not reopen the conversation. Your engagement brief is safe.',
    },
    component: EngagementCharterSurface,
  },
  // 075 brief-first screens: every screen is a capture surface + public JSON
  // structure + a facilitator that is the existing chat. Facts land directly;
  // the surface is inline-editable (no propose/accept ceremony).
  'engagement-brief-surface': {
    id: 'engagement-brief-surface',
    title: 'Brief',
    workspaceLabel: 'The brief',
    chatLabel: 'Brief conversation',
    emptyChatText: 'Talk or paste anything you have. The brief and the plan build beside the conversation.',
    about: [
      'Narrate the engagement — background, what the client wants, who is involved. The brief fills in as you talk, and activities the engagement needs are pulled into the plan with the reason they were suggested.',
      'Everything recorded is yours to edit in place, and you can add or remove activities from the plan at any time.',
    ],
    chatCopy: {
      openingFallback: () => 'What is the background to this engagement? Talk or paste anything you have — I will build the brief and a first plan beside our conversation.',
      sendFailure: 'Your message was not sent. The brief is unchanged; try again.',
      resumeFallback: 'Welcome back. The brief is where we left it — carry on.',
      resumeFailure: 'We could not resume the conversation. The brief is still available; try again shortly.',
    },
    component: BriefOutcomeSurface,
    // 077: the Brief listens — no spoken replies while the consultant dumps.
    voice: { speakReplies: false },
  },
  'objectives-surface': {
    id: 'objectives-surface',
    title: 'Objectives',
    workspaceLabel: 'Objectives',
    chatLabel: 'Objectives conversation',
    emptyChatText: 'Talk through what the engagement must achieve. Objectives fill in beside the conversation.',
    about: [
      'This session firms up the objectives: the real challenge underneath (diagnosis), the overall approach (guiding policy), and each objective\'s what, why, who and success measure.',
      'Anything captured from the brief is already here, marked "from Brief", and everything is yours to edit in place.',
    ],
    chatCopy: {
      openingFallback: () => 'From the brief, let\'s firm up what success means. What does the client most need this engagement to achieve?',
      sendFailure: 'Your message was not sent. The objectives are unchanged; try again.',
      resumeFallback: 'Welcome back. The objectives are where we left them — carry on.',
      resumeFailure: 'We could not resume the conversation. The objectives are still available; try again shortly.',
    },
    component: ObjectivesOutcomeSurface,
  },
  // 095: the business map — what the business does (processes, grouped
  // under domains), who is involved, and what matters most (importance and
  // storm order), so Event Storming and VSM start in the right place.
  'domains-stakeholders-surface': {
    id: 'domains-stakeholders-surface',
    title: 'Business map',
    workspaceLabel: 'Business map',
    chatLabel: 'Business map conversation',
    emptyChatText: 'Talk through what the business does, who is involved, and what matters most.',
    about: [
      'Three questions build the map: what does the business do (processes, grouped under the domains that own them), who is involved, and what matters most (importance and storm order).',
      'Anything captured from the brief is already here, marked "from Brief", and everything is yours to edit in place.',
    ],
    chatCopy: {
      openingFallback: () => 'What does the business do? Talk me through the processes, and who is involved.',
      sendFailure: 'Your message was not sent. The business map is unchanged; try again.',
      resumeFallback: 'Welcome back. The business map is where we left it — carry on.',
      resumeFailure: 'We could not resume the conversation. The business map is still available; try again shortly.',
    },
    component: BusinessMapSurface,
  },
  // 082: ONE generic surface serves every notes-shaped catalogue activity.
  // The workspace parameterises it with the activity key; nothing here names
  // an activity.
  'activity-notes-surface': {
    id: 'activity-notes-surface',
    title: 'Activity notes',
    workspaceLabel: 'Captured notes',
    chatLabel: 'Activity conversation',
    emptyChatText: 'Talk through this activity. Its notes build beside the conversation.',
    about: [
      'This activity\'s notes were seeded from the Brief conversation and grow as you work it through here.',
      'Everything recorded is yours to edit in place, add to, or remove.',
    ],
    chatCopy: {
      openingFallback: () => 'Where would you like to start with this activity? Anything already captured is beside our conversation.',
      sendFailure: 'Your message was not sent. The notes are unchanged; try again.',
      resumeFallback: 'Welcome back. The notes are where we left them. Carry on.',
      resumeFailure: 'We could not resume the conversation. The notes are still available; try again shortly.',
    },
    component: ActivityNotesSurface,
  },
};

export function surfaceForId(surfaceId?: string): SurfaceDefinition | undefined {
  return surfaceId ? surfaces[surfaceId] : undefined;
}

export const registeredSurfaceIds = Object.freeze(Object.keys(surfaces));
